import { TRPCClientError } from "@trpc/client";
import { TRPCError } from "@trpc/server";
import { NoAuthError } from "@utils/NoAuthError";
import { SessionEndedError } from "./sessionLink";
import { InvalidOptionsError } from "@utils/InvalidOptionsError";

export enum EXPECTED_TRPC_ERROR_TYPE {
  no_firebase_token,
  no_user_found,
  user_provisioning_incomplete,
}

const EXPECTED_TRPC_ERROR_TYPES = Object.values(
  EXPECTED_TRPC_ERROR_TYPE,
) as EXPECTED_TRPC_ERROR_TYPE[];

const expBackoff = (failureCount: number) => {
  const baseDelayMs = 100;
  const delayMs = baseDelayMs * Math.pow(2, failureCount);
  return delayMs;
};

export const isExpectedTRPCError = (error: unknown) => {
  if (error instanceof TRPCClientError) {
    if (
      error.meta?.type &&
      EXPECTED_TRPC_ERROR_TYPES.includes(
        error.meta.type as EXPECTED_TRPC_ERROR_TYPE,
      )
    ) {
      return true;
    }

    const data = error.data as
      { code?: string; httpStatus?: number } | undefined;
    if (
      data?.code === "PRECONDITION_FAILED" &&
      error.message === "User provisioning is not complete yet"
    ) {
      return true;
    }
  }
  if (error instanceof TRPCError) {
    const code = error.code;
    const message = error.message;
    if (code === "NOT_FOUND" && message === "no_user_found") {
      return true;
    }
    if (
      code === "PRECONDITION_FAILED" &&
      message === "User provisioning is not complete yet"
    ) {
      return true;
    }
  }

  return false;
};

const isUnexpectedError = (error: unknown) => {
  if (isExpectedTRPCError(error)) {
    return false;
  }

  return true;
};

export const retryOptions = {
  retry: (failureCount: number, error: any) => {
    if (
      error instanceof TRPCClientError &&
      error.cause instanceof SessionEndedError
    )
      return false;
    if (error instanceof NoAuthError) {
      console.log(
        `::queryClient retryOptions:: NoAuthError: ${error.caller} - skipping retry`,
      );
      return false;
    }
    if (error.message.includes("Missing queryFn")) {
      console.log(
        `::queryClient retryOptions:: Missing queryFn - skipping retry`,
      );
      return false;
    }

    if (error instanceof InvalidOptionsError) {
      console.log(
        `::queryClient retryOptions:: InvalidOptionsError: ${error.caller} - skipping retry`,
      );
      return false;
    }

    if (isUnexpectedError(error)) {
      return failureCount < 2;
    }
    return failureCount < 5;
  },
  retryDelay(failureCount: number, error: any) {
    let delayMs = 0;
    const isUnexpected = isUnexpectedError(error);
    if (isUnexpected) {
      delayMs = 750;
    } else {
      delayMs = expBackoff(failureCount);
    }

    if (error instanceof NoAuthError) {
      return 0;
    }

    console.log(
      `Retry attempt ${failureCount} failed with error ${error}, retrying in ${delayMs}ms`,
    );
    return delayMs;
  },
};
